import React, { useEffect, useState } from 'react'
import {
  CButton,
  CCol,
  CRow,
  CSmartTable,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody,
  CFormLabel,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
} from '@coreui/react-pro'
import AsyncSelect from 'react-select/async'
import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent.js'
import ChannelAccessService from 'src/service/ChannelAccessService'

const toOptions = (users = []) =>
  users.map((user) => ({
    value: user.id,
    label: `${user.full_name} (${user.email})`,
  }))

const ChannelAccess = () => {
  const [loading, setLoading] = useState(true)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState([])
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState([])

  const [selectedUsers, setSelectedUsers] = useState([])
  const [defaultOptions, setDefaultOptions] = useState([])
  const [saving, setSaving] = useState(false)
  const [removeItem, setRemoveItem] = useState(null)

  const columns = [
    { label: 'Name', key: 'full_name' },
    { label: 'Email', key: 'email' },
    { label: 'Given By', key: 'granted_by_name', filter: false, sorter: false },
    { label: 'Given On', key: 'created_at', filter: false },
    { key: 'action', label: 'Action', filter: false, sorter: false },
  ]

  useEffect(() => {
    fetchData(activePage, columnFilter, columnSorter, itemsPerPage)
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  useEffect(() => {
    loadDefaultOptions()
  }, [])

  const fetchData = (activePage, columnFilter, columnSorter, itemsPerPage) => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    ChannelAccessService.getAccessList(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setUsers(result.data)
      })
      .catch((err) =>
        ToastComponent(
          err?.response?.data?.message || 'Unable to load channel access list',
          'error',
        ),
      )
      .finally(() => setLoading(false))
  }

  const loadDefaultOptions = () => {
    ChannelAccessService.getUserOptions()
      .then((res) => setDefaultOptions(toOptions(res.data)))
      .catch(() => setDefaultOptions([]))
  }

  // Server-side search so the dropdown stays fast with thousands of members.
  const loadOptions = (inputValue) =>
    ChannelAccessService.getUserOptions(inputValue)
      .then((res) => toOptions(res.data))
      .catch(() => [])

  const refresh = () => {
    fetchData(activePage, columnFilter, columnSorter, itemsPerPage)
    loadDefaultOptions()
  }

  const handleGrant = () => {
    if (!selectedUsers.length) {
      ToastComponent('Please select at least one member.', 'error')
      return
    }
    setSaving(true)
    ChannelAccessService.grantAccess({ user_ids: selectedUsers.map((u) => u.value) })
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
          setSelectedUsers([])
          refresh()
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch((err) =>
        ToastComponent(err?.response?.data?.message || 'Something went wrong', 'error'),
      )
      .finally(() => setSaving(false))
  }

  const handleRevoke = () => {
    ChannelAccessService.revokeAccess({ id: removeItem.id })
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
          refresh()
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch((err) =>
        ToastComponent(err?.response?.data?.message || 'Something went wrong', 'error'),
      )
      .finally(() => setRemoveItem(null))
  }

  return (
    <>
      <CRow>
        <CAccordion activeItemKey={1} alwaysOpen>
          <CAccordionItem itemKey={1}>
            <CAccordionHeader>
              <strong>Give Channel Access</strong>
            </CAccordionHeader>
            <CAccordionBody>
              <CRow className="align-items-end">
                <CCol md={9}>
                  <CFormLabel>Members</CFormLabel>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    defaultOptions={defaultOptions}
                    loadOptions={loadOptions}
                    value={selectedUsers}
                    onChange={(value) => setSelectedUsers(value || [])}
                    placeholder="Search member by name or email..."
                    noOptionsMessage={() => 'No members found'}
                  />
                </CCol>
                <CCol md={3} className="mt-3 mt-md-0">
                  <CButton
                    color="primary"
                    className="w-100"
                    onClick={handleGrant}
                    disabled={saving || !selectedUsers.length}
                  >
                    {saving ? 'Saving...' : 'Give Access'}
                  </CButton>
                </CCol>
              </CRow>
            </CAccordionBody>
          </CAccordionItem>

          <CAccordionItem itemKey={2}>
            <CAccordionHeader>
              <strong>Members With Channel Access</strong>
            </CAccordionHeader>
            <CAccordionBody>
              <CSmartTable
                columns={columns}
                columnFilter={{ external: true }}
                columnSorter={{ external: true }}
                scopedColumns={{
                  granted_by_name: (item) => <td>{item.granted_by_name || '-'}</td>,
                  created_at: (item) => (
                    <td>{item.created_at ? moment(item.created_at).format('D MMM YYYY') : '-'}</td>
                  ),
                  action: (item) => (
                    <td>
                      <CButton
                        color="danger"
                        variant="outline"
                        shape="square"
                        size="sm"
                        onClick={() => setRemoveItem(item)}
                      >
                        Remove
                      </CButton>
                    </td>
                  ),
                }}
                items={users?.data}
                itemsPerPage={itemsPerPage}
                itemsPerPageSelect
                loading={loading}
                noItemsLabel="No member has channel access yet"
                pagination={{ external: true }}
                paginationProps={{
                  activePage: activePage,
                  pages: Math.ceil(users?.total / itemsPerPage) || 1,
                }}
                tableProps={{ hover: true, responsive: true }}
                onActivePageChange={(activePage) => setActivePage(activePage)}
                onColumnFilterChange={(filter) => {
                  setActivePage(1)
                  setColumnFilter(filter)
                }}
                onItemsPerPageChange={(itemsPerPage) => {
                  setActivePage(1)
                  setItemsPerPage(itemsPerPage)
                }}
                onSorterChange={(sorter) => setColumnSorter(sorter)}
              />
            </CAccordionBody>
          </CAccordionItem>
        </CAccordion>
      </CRow>

      <CModal visible={!!removeItem} onClose={() => setRemoveItem(null)} backdrop="static">
        <CModalHeader>
          <strong>Remove Channel Access</strong>
        </CModalHeader>
        <CModalBody>
          Remove channel access for <strong>{removeItem?.full_name}</strong>? They will no longer
          see the Channel menu in the app.
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setRemoveItem(null)}>
            Cancel
          </CButton>
          <CButton color="danger" onClick={handleRevoke}>
            Remove
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default ChannelAccess
